import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { authAPI } from '../services/api';
import SEOHead from '../components/SEOHead';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    email: searchParams.get('email') || '',
    code: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    number: false,
    special: false,
    upper: false,
    lower: false
  });

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // If no email provided, redirect to forgot password
    if (!formData.email) {
      navigate('/forgot-password');
      return;
    }
  }, [formData.email, navigate]);

  // Password validation
  useEffect(() => {
    const password = formData.newPassword;
    setPasswordStrength({
      length: password.length >= 8,
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
      upper: /[A-Z]/.test(password),
      lower: /[a-z]/.test(password)
    });
  }, [formData.newPassword]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'code') {
      // Only allow digits and limit to 6 characters
      const digits = value.replace(/\D/g, '').slice(0, 6);
      setFormData(prev => ({ ...prev, [name]: digits }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const isPasswordValid = Object.values(passwordStrength).every(Boolean);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.code.length !== 6) {
      setError('Please enter a 6-digit verification code');
      return;
    }

    if (!isPasswordValid) {
      setError('Please meet all password requirements');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      await authAPI.resetPassword(formData.email, formData.code, formData.newPassword);
      setSuccess(true);
      
      setTimeout(() => {
        navigate('/login', { 
          state: { 
            message: 'Password reset successfully! Please log in with your new password.' 
          } 
        });
      }, 3000);
      
    } catch (error) {
      setError(error.response?.data?.detail || 'Password reset failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-purple-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <SEOHead 
          title="Password Reset Successful - BrixBot"
          description="Password reset completed successfully"
          isIndexable={false}
        />
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto h-24 w-24 bg-white rounded-full flex items-center justify-center mb-6">
              <span className="text-4xl">✅</span>
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">
              Password Reset Successful!
            </h2>
            <p className="text-purple-200">
              Your password has been updated successfully.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-xl p-8 text-center">
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg mb-6">
              <div className="flex items-center justify-center">
                <span className="text-green-500 mr-2 text-xl">🎉</span>
                Password has been reset successfully!
              </div>
            </div>
            
            <p className="text-gray-600 mb-6">
              You will be redirected to the login page shortly to sign in with your new password.
            </p>
            
            <Link
              to="/login"
              className="w-full bg-purple-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-purple-700 transition duration-200 inline-block"
            >
              Continue to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-purple-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <SEOHead 
        title="Reset Password - BrixBot"
        description="Reset your BrixBot account password"
        isIndexable={false}
      />
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-24 w-24 bg-white rounded-full flex items-center justify-center mb-6">
            <span className="text-4xl">🔑</span>
          </div>
          <h2 className="text-3xl font-bold text-white mb-2">
            Reset Your Password
          </h2>
          <p className="text-purple-200">
            Enter the code sent to {formData.email}
          </p>
        </div>

        {/* Reset Form */}
        <div className="bg-white rounded-xl shadow-xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Verification Code Input */}
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2">
                6-digit verification code
              </label>
              <input
                id="code"
                name="code"
                type="text"
                value={formData.code}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-center text-2xl font-bold tracking-widest text-gray-900 bg-white"
                placeholder="000000"
                maxLength="6"
                required
              />
            </div>

            {/* New Password Input */}
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                New Password
              </label>
              <input
                id="newPassword"
                name="newPassword"
                type="password"
                value={formData.newPassword}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 bg-white"
                placeholder="Enter new password"
                required
              />
              
              {/* Password Requirements */}
              {formData.newPassword && (
                <div className="mt-2 text-xs space-y-1">
                  <div className={`flex items-center ${passwordStrength.length ? 'text-green-600' : 'text-red-600'}`}>
                    <span className="mr-2">{passwordStrength.length ? '✓' : '✗'}</span>
                    At least 8 characters
                  </div>
                  <div className={`flex items-center ${passwordStrength.number ? 'text-green-600' : 'text-red-600'}`}>
                    <span className="mr-2">{passwordStrength.number ? '✓' : '✗'}</span>
                    Contains a number
                  </div>
                  <div className={`flex items-center ${passwordStrength.special ? 'text-green-600' : 'text-red-600'}`}>
                    <span className="mr-2">{passwordStrength.special ? '✓' : '✗'}</span>
                    Contains a special character
                  </div>
                  <div className={`flex items-center ${passwordStrength.upper ? 'text-green-600' : 'text-red-600'}`}>
                    <span className="mr-2">{passwordStrength.upper ? '✓' : '✗'}</span>
                    Contains an uppercase letter
                  </div>
                  <div className={`flex items-center ${passwordStrength.lower ? 'text-green-600' : 'text-red-600'}`}>
                    <span className="mr-2">{passwordStrength.lower ? '✓' : '✗'}</span>
                    Contains a lowercase letter
                  </div>
                </div>
              )}
            </div>

            {/* Confirm Password Input */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                Confirm New Password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 bg-white"
                placeholder="Confirm new password"
                required
              />
              
              {/* Password Match Indicator */}
              {formData.confirmPassword && (
                <div className={`mt-2 text-xs ${formData.newPassword === formData.confirmPassword ? 'text-green-600' : 'text-red-600'}`}>
                  <span className="mr-2">{formData.newPassword === formData.confirmPassword ? '✓' : '✗'}</span>
                  Passwords {formData.newPassword === formData.confirmPassword ? 'match' : 'do not match'}
                </div>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
                <div className="flex items-center">
                  <span className="text-red-500 mr-2">⚠️</span>
                  {error}
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || formData.code.length !== 6 || !isPasswordValid || formData.newPassword !== formData.confirmPassword}
              className="w-full bg-purple-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Resetting Password...
                </div>
              ) : (
                'Reset Password'
              )}
            </button>
          </form>

          {/* Additional Info */}
          <div className="mt-6 text-center text-sm text-gray-500">
            <p>The verification code will expire in 30 minutes.</p>
            <p className="mt-2">
              Didn't receive the code?{' '}
              <Link to="/forgot-password" className="text-purple-600 hover:text-purple-800">
                Request a new one
              </Link>
            </p>
          </div>
        </div>

        {/* Back to Login Link */}
        <div className="text-center">
          <Link
            to="/login"
            className="text-purple-200 hover:text-white transition duration-200"
          >
            ← Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;